import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAddToCartCardKeyToProcess, setOpenTab, setView } from '../../store/actions';
import { getKeyFromStorageAsync, saveKeyToStorageAsync } from '../../store/savedState';
import { getPendingAddToCartCardKey } from '../../store/selectors';
import { View } from '../../store/typings';
import Translation from '../Translation';
import { NewSkinLoader, SuccessCheck } from '../svgIcons/SvgIcons';
import styles from './AddToCartButton.styles';
import { ADD_TO_CART_CLICKED_KEYS } from './contants';

const ADDING_MIN_DURATION_MS = 2000;
const ADDED_DURATION_MS = 3000;

interface AddToCartButtonProps {
    productVariantId: number;
    messageId: string;
    disabled?: boolean;
    onAddToCart: () => Promise<boolean>;
    isFlyMessage?: boolean;
}

const AddToCartButton = ({
    productVariantId,
    messageId,
    disabled = false,
    onAddToCart,
    isFlyMessage,
}: AddToCartButtonProps): ReactElement | null => {
    const dispatch = useDispatch();
    const pendingAddToCartCardKey = useSelector(getPendingAddToCartCardKey);
    const cardKey = `${messageId}_${productVariantId}`;
    const [buttonState, setButtonState] = useState<'add' | 'adding' | 'added' | 'cart'>('add');
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

    const handleAddToCartInFlyMessage = useCallback(async (): Promise<void> => {
        dispatch(setView(View.CHAT));
        dispatch(setOpenTab('conversations'));
        dispatch(setAddToCartCardKeyToProcess(cardKey));
    }, [dispatch, cardKey]);

    const handleAddToCartClick = useCallback(async (): Promise<boolean> => {
        if (isFlyMessage) {
            handleAddToCartInFlyMessage();
            return false;
        }

        setButtonState('adding');
        const [isAddedToCart] = await Promise.all([
            onAddToCart(),
            new Promise((resolve) => {
                setTimeout(resolve, ADDING_MIN_DURATION_MS);
            }),
        ]);

        if (isAddedToCart) {
            let newStoredCardKeys: string[] = [];

            try {
                const storedCardKeys = await getKeyFromStorageAsync(ADD_TO_CART_CLICKED_KEYS);
                if (Array.isArray(storedCardKeys)) {
                    newStoredCardKeys = [...storedCardKeys, cardKey];
                } else {
                    newStoredCardKeys = [cardKey];
                }
            } catch {
                newStoredCardKeys = [cardKey];
            }

            await saveKeyToStorageAsync(ADD_TO_CART_CLICKED_KEYS, newStoredCardKeys);
            setButtonState('added');

            timeoutIdRef.current = setTimeout(() => {
                timeoutIdRef.current = undefined;
                setButtonState('cart');
            }, ADDED_DURATION_MS);
        } else {
            setButtonState('add');
        }
        return true;
    }, [handleAddToCartInFlyMessage, isFlyMessage, cardKey, onAddToCart]);

    const handleViewCartClick = (): void => {
        try {
            if (window.top) {
                const { origin } = window.top.location;
                window.top.location.assign(`${origin}/cart`);
            }
        } catch {
            //
        }
    };

    useEffect(() => {
        if (!isFlyMessage && pendingAddToCartCardKey !== null) {
            dispatch(setAddToCartCardKeyToProcess(null));

            if (pendingAddToCartCardKey === cardKey) {
                handleAddToCartClick();
            }
        }
    }, [pendingAddToCartCardKey, cardKey, dispatch, handleAddToCartClick, isFlyMessage]);

    useEffect(() => {
        getKeyFromStorageAsync(ADD_TO_CART_CLICKED_KEYS).then((storedCardKeys) => {
            if (Array.isArray(storedCardKeys) && storedCardKeys.includes(cardKey)) {
                setButtonState('cart');
            }
        });
    }, [cardKey]);

    useEffect(
        () => (): void => {
            if (timeoutIdRef.current !== undefined) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = undefined;
            }
        },
        [],
    );

    if (buttonState === 'add') {
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={handleAddToCartClick}
                css={styles.primaryButton}
            >
                <Translation value="recommendProductAddToCart" fallback="Add to cart" />
            </button>
        );
    }
    if (buttonState === 'adding') {
        return (
            <button type="button" disabled css={styles.addingToCartButton}>
                <NewSkinLoader width={20} height={20} />

                <Translation value="recommendProductAddingToCart" fallback="Adding to cart" />
            </button>
        );
    }

    if (buttonState === 'added') {
        return (
            <button type="button" disabled css={styles.addedToCartButton}>
                <SuccessCheck />
                <Translation value="recommendProductAdded" fallback="Added" />
            </button>
        );
    }

    if (buttonState === 'cart') {
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={handleViewCartClick}
                css={styles.primaryButton}
            >
                <Translation value="recommendProductViewCart" fallback="View cart" />
            </button>
        );
    }

    return null;
};

export default AddToCartButton;
